import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminNav } from "../../components/admin/AdminNav";
import { FileUpload } from "../../components/admin/FileUpload";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, ArrowLeft, GripVertical } from "lucide-react";

const EMPTY = {
  category: "", title: "", subtitle: "", slug: "", status: "active",
  price: "", seats: "", enroll_online_url: "",
  partial_enabled: false, partial_title: "", partial_description: "", partial_price: "", partial_url: "", partial_features: [],
  speaker_name: "", speaker_qualification: "", speaker_claim: "", speaker_bio: "", speaker_photo: "",
  speaker_social: [],
  date_time: "", city: "", location_name: "", location_address: "", location_description: "", location_map_url: "",
  hero_image: "", gallery: [], videos: [],
  why_participate: [], program: [], faq: [],
  meta_title: "", meta_description: "",
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(val) {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d) ? "" : d.toISOString();
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      {label && <label className="overline mb-1 block">{label}</label>}
      <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="editorial-input" />
    </div>
  );
}
function Area({ label, value, onChange, placeholder, rows = 3, testid }) {
  return (
    <div>
      {label && <label className="overline mb-1 block">{label}</label>}
      <textarea data-testid={testid} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="editorial-input resize-none" />
    </div>
  );
}
function SectionCard({ children }) {
  return <div className="bg-[#E9EFFA] border border-[#D5DEEE] rounded-sm p-5 relative">{children}</div>;
}

export default function CourseEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [c, setC] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/courses/${id}`).then((r) => { setC({ ...EMPTY, ...r.data }); setLoading(false); }).catch(() => { toast.error("Corso non trovato"); nav("/admin"); });
  }, [id]);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const updItem = (k, i, patch) => setC((p) => ({ ...p, [k]: p[k].map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const addItem = (k, tmpl) => setC((p) => ({ ...p, [k]: [...p[k], tmpl] }));
  const rmItem = (k, i) => setC((p) => ({ ...p, [k]: p[k].filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!c.title) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    const payload = { ...c, date_time: c.date_time };
    try {
      if (isNew) {
        const { data } = await api.post("/admin/courses", payload);
        toast.success("Corso creato");
        nav(`/admin/corsi/${data.id}`);
      } else {
        await api.put(`/admin/courses/${id}`, payload);
        toast.success("Modifiche salvate");
      }
    } catch (err) {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F8FF]">
        <AdminNav />
        <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F8FF] pb-28">
      <AdminNav />
      <div className="px-6 md:px-10 max-w-[1100px] mx-auto py-8">
        <button onClick={() => nav("/admin")} className="inline-flex items-center gap-2 text-sm text-[#5C6678] hover:text-[#0B1220] mb-4">
          <ArrowLeft className="h-4 w-4" /> Torna ai corsi
        </button>
        <h1 className="font-heading text-4xl font-light text-[#0B1220] mb-8" data-testid="editor-title">
          {isNew ? "Nuovo corso" : c.title || "Modifica corso"}
        </h1>

        <Tabs defaultValue="generale">
          <TabsList className="flex flex-wrap h-auto bg-[#E9EFFA] p-1 gap-1">
            <TabsTrigger value="generale" data-testid="tab-generale">Generale</TabsTrigger>
            <TabsTrigger value="parte" data-testid="tab-parte">Parte online</TabsTrigger>
            <TabsTrigger value="docente" data-testid="tab-docente">Docente</TabsTrigger>
            <TabsTrigger value="date" data-testid="tab-date">Date &amp; Sede</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="programma" data-testid="tab-programma">Programma</TabsTrigger>
            <TabsTrigger value="faq" data-testid="tab-faq">FAQ</TabsTrigger>
            <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
          </TabsList>

          {/* GENERALE */}
          <TabsContent value="generale" className="space-y-6 pt-8">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Categoria" value={c.category} onChange={(v) => set("category", v)} placeholder="Es. Implantologia" testid="f-category" />
              <div>
                <label className="overline mb-1 block">Stato</label>
                <Select value={c.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="f-status" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Attivo</SelectItem>
                    <SelectItem value="inactive">Disattivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Field label="Titolo del corso *" value={c.title} onChange={(v) => set("title", v)} placeholder="Titolo" testid="f-title" />
            <Area label="Sottotitolo" value={c.subtitle} onChange={(v) => set("subtitle", v)} placeholder="Breve descrizione" testid="f-subtitle" />
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Prezzo" value={c.price} onChange={(v) => set("price", v)} placeholder="Es. € 1.290 + IVA" testid="f-price" />
              <Field label="Posti disponibili" value={c.seats} onChange={(v) => set("seats", v)} placeholder="Es. 20 posti disponibili" testid="f-seats" />
            </div>
            <Field label="Link iscrizione online (sito esterno)" value={c.enroll_online_url} onChange={(v) => set("enroll_online_url", v)} placeholder="https://..." testid="f-enroll-url" />
          </TabsContent>

          {/* PARTE ONLINE */}
          <TabsContent value="parte" className="space-y-6 pt-8">
            <div className="flex items-center justify-between bg-[#E9EFFA] border border-[#D5DEEE] rounded-sm p-4">
              <div className="pr-4">
                <div className="font-medium text-[#0B1220]">Abilita acquisto di una parte online</div>
                <div className="text-sm text-[#5C6678]">Mostra una sezione per acquistare solo una parte del corso (es. modulo teorico) tramite sito esterno.</div>
              </div>
              <Switch data-testid="f-partial-enabled" checked={!!c.partial_enabled} onCheckedChange={(v) => set("partial_enabled", v)} />
            </div>

            <div className={c.partial_enabled ? "space-y-6" : "space-y-6 opacity-50 pointer-events-none"}>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Titolo parte" value={c.partial_title} onChange={(v) => set("partial_title", v)} placeholder="Es. Modulo Online — Parte Teorica" testid="f-partial-title" />
                <Field label="Prezzo parte" value={c.partial_price} onChange={(v) => set("partial_price", v)} placeholder="Es. € 290 + IVA" testid="f-partial-price" />
              </div>
              <Area label="Descrizione" value={c.partial_description} onChange={(v) => set("partial_description", v)} placeholder="Cosa include la parte acquistabile online" testid="f-partial-desc" />
              <Field label="Link acquisto (sito esterno)" value={c.partial_url} onChange={(v) => set("partial_url", v)} placeholder="https://..." testid="f-partial-url" />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="overline">Cosa include</label>
                  <button onClick={() => addItem("partial_features", "")} className="inline-flex items-center gap-1 text-sm text-[#1452F0]" data-testid="add-partial-feature"><Plus className="h-4 w-4" /> Aggiungi voce</button>
                </div>
                <div className="space-y-3">
                  {c.partial_features.map((f, i) => (
                    <div key={i} className="flex gap-3 items-center">
                      <input value={f} onChange={(e) => setC((p) => ({ ...p, partial_features: p.partial_features.map((x, idx) => (idx === i ? e.target.value : x)) }))} placeholder="Es. Accesso alle videolezioni on-demand" className="editorial-input flex-1" />
                      <button onClick={() => rmItem("partial_features", i)} className="text-[#A63C3C] p-2"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* DOCENTE */}
          <TabsContent value="docente" className="space-y-6 pt-8">
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="space-y-6">
                <Field label="Nome relatore" value={c.speaker_name} onChange={(v) => set("speaker_name", v)} placeholder="Dott. ..." testid="f-speaker-name" />
                <Field label="Qualifica" value={c.speaker_qualification} onChange={(v) => set("speaker_qualification", v)} placeholder="Specialista in..." testid="f-speaker-qual" />
                <Area label="Claim autorevole" value={c.speaker_claim} onChange={(v) => set("speaker_claim", v)} placeholder="Breve frase d'impatto" rows={2} testid="f-speaker-claim" />
              </div>
              <FileUpload label="Foto relatore" value={c.speaker_photo} onChange={(v) => set("speaker_photo", v)} testid="f-speaker-photo" height="h-72" />
            </div>
            <Area label="Bio / esperienza" value={c.speaker_bio} onChange={(v) => set("speaker_bio", v)} placeholder="Biografia, esperienza, autorevolezza" rows={5} testid="f-speaker-bio" />

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="overline">Link social / sito</label>
                <button onClick={() => addItem("speaker_social", { label: "", url: "" })} className="inline-flex items-center gap-1 text-sm text-[#1452F0]" data-testid="add-social"><Plus className="h-4 w-4" /> Aggiungi</button>
              </div>
              <div className="space-y-3">
                {c.speaker_social.map((s, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <input value={s.label} onChange={(e) => updItem("speaker_social", i, { label: e.target.value })} placeholder="Etichetta (es. LinkedIn)" className="editorial-input flex-1" />
                    <input value={s.url} onChange={(e) => updItem("speaker_social", i, { url: e.target.value })} placeholder="https://..." className="editorial-input flex-[2]" />
                    <button onClick={() => rmItem("speaker_social", i)} className="text-[#A63C3C] p-2"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* DATE & SEDE */}
          <TabsContent value="date" className="space-y-6 pt-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="overline mb-1 block">Data e ora evento</label>
                <input data-testid="f-datetime" type="datetime-local" value={toLocalInput(c.date_time)} onChange={(e) => set("date_time", fromLocalInput(e.target.value))} className="editorial-input" />
              </div>
              <Field label="Città" value={c.city} onChange={(v) => set("city", v)} placeholder="Es. Milano" testid="f-city" />
            </div>
            <Field label="Nome sede" value={c.location_name} onChange={(v) => set("location_name", v)} placeholder="Es. Centro Congressi..." testid="f-loc-name" />
            <Field label="Indirizzo" value={c.location_address} onChange={(v) => set("location_address", v)} placeholder="Via, numero, CAP, città" testid="f-loc-address" />
            <Area label="Descrizione sede" value={c.location_description} onChange={(v) => set("location_description", v)} placeholder="Breve descrizione della location" testid="f-loc-desc" />
            <Field label="URL mappa (embed, opzionale)" value={c.location_map_url} onChange={(v) => set("location_map_url", v)} placeholder="https://www.google.com/maps?q=...&output=embed" testid="f-loc-map" />
          </TabsContent>

          {/* MEDIA */}
          <TabsContent value="media" className="space-y-8 pt-8">
            <FileUpload label="Immagine principale (hero / card)" value={c.hero_image} onChange={(v) => set("hero_image", v)} testid="f-hero" height="h-60" />

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="overline">Gallery clinica</label>
                <button onClick={() => addItem("gallery", "")} className="inline-flex items-center gap-1 text-sm text-[#1452F0]" data-testid="add-gallery"><Plus className="h-4 w-4" /> Aggiungi immagine</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {c.gallery.map((g, i) => (
                  <div key={i} className="relative">
                    <FileUpload value={g} onChange={(v) => setC((p) => ({ ...p, gallery: p.gallery.map((x, idx) => (idx === i ? v : x)) }))} testid={`f-gallery-${i}`} height="h-36" />
                    <button onClick={() => rmItem("gallery", i)} className="absolute -top-2 -right-2 bg-[#A63C3C] text-white rounded-full p-1.5 z-10"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="overline">Video (YouTube / Vimeo / upload)</label>
                <button onClick={() => addItem("videos", { title: "", type: "youtube", url: "" })} className="inline-flex items-center gap-1 text-sm text-[#1452F0]" data-testid="add-video"><Plus className="h-4 w-4" /> Aggiungi video</button>
              </div>
              <div className="space-y-4">
                {c.videos.map((v, i) => (
                  <SectionCard key={i}>
                    <button onClick={() => rmItem("videos", i)} className="absolute top-3 right-3 text-[#A63C3C]"><Trash2 className="h-4 w-4" /></button>
                    <div className="grid md:grid-cols-3 gap-4">
                      <Field label="Titolo" value={v.title} onChange={(val) => updItem("videos", i, { title: val })} placeholder="Es. Video teaser" />
                      <div>
                        <label className="overline mb-1 block">Tipo</label>
                        <Select value={v.type} onValueChange={(val) => updItem("videos", i, { type: val })}>
                          <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="youtube">YouTube</SelectItem>
                            <SelectItem value="vimeo">Vimeo</SelectItem>
                            <SelectItem value="upload">File caricato</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-1">
                        {v.type === "upload" ? (
                          <FileUpload label="File video" accept="video/*" value={v.url} onChange={(val) => updItem("videos", i, { url: val })} height="h-24" />
                        ) : (
                          <Field label="URL embed" value={v.url} onChange={(val) => updItem("videos", i, { url: val })} placeholder="https://www.youtube.com/embed/..." />
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[#5C6678] mt-2">Per YouTube/Vimeo usa il link <em>embed</em> (es. youtube.com/embed/ID).</p>
                  </SectionCard>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* PROGRAMMA */}
          <TabsContent value="programma" className="space-y-8 pt-8">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="overline">Perché partecipare</label>
                <button onClick={() => addItem("why_participate", { title: "", text: "" })} className="inline-flex items-center gap-1 text-sm text-[#1452F0]" data-testid="add-benefit"><Plus className="h-4 w-4" /> Aggiungi blocco</button>
              </div>
              <div className="space-y-4">
                {c.why_participate.map((b, i) => (
                  <SectionCard key={i}>
                    <button onClick={() => rmItem("why_participate", i)} className="absolute top-3 right-3 text-[#A63C3C]"><Trash2 className="h-4 w-4" /></button>
                    <Field label="Titolo" value={b.title} onChange={(val) => updItem("why_participate", i, { title: val })} />
                    <div className="mt-3"><Area label="Testo" value={b.text} onChange={(val) => updItem("why_participate", i, { text: val })} rows={2} /></div>
                  </SectionCard>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="overline">Programma (per giornata)</label>
                <button onClick={() => addItem("program", { title: "", time: "", topics: "", practice: "" })} className="inline-flex items-center gap-1 text-sm text-[#1452F0]" data-testid="add-day"><Plus className="h-4 w-4" /> Aggiungi giornata</button>
              </div>
              <div className="space-y-4">
                {c.program.map((d, i) => (
                  <SectionCard key={i}>
                    <button onClick={() => rmItem("program", i)} className="absolute top-3 right-3 text-[#A63C3C]"><Trash2 className="h-4 w-4" /></button>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field label="Titolo giornata" value={d.title} onChange={(val) => updItem("program", i, { title: val })} placeholder="Es. Giorno 1 — ..." />
                      <Field label="Orari" value={d.time} onChange={(val) => updItem("program", i, { time: val })} placeholder="Es. 09:00 – 18:00" />
                    </div>
                    <div className="mt-3"><Area label="Argomenti" value={d.topics} onChange={(val) => updItem("program", i, { topics: val })} rows={3} /></div>
                    <div className="mt-3"><Area label="Attività pratiche" value={d.practice} onChange={(val) => updItem("program", i, { practice: val })} rows={2} /></div>
                  </SectionCard>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* FAQ */}
          <TabsContent value="faq" className="space-y-4 pt-8">
            <div className="flex items-center justify-between mb-1">
              <label className="overline">Domande frequenti</label>
              <button onClick={() => addItem("faq", { question: "", answer: "" })} className="inline-flex items-center gap-1 text-sm text-[#1452F0]" data-testid="add-faq"><Plus className="h-4 w-4" /> Aggiungi FAQ</button>
            </div>
            {c.faq.map((f, i) => (
              <SectionCard key={i}>
                <button onClick={() => rmItem("faq", i)} className="absolute top-3 right-3 text-[#A63C3C]"><Trash2 className="h-4 w-4" /></button>
                <Field label="Domanda" value={f.question} onChange={(val) => updItem("faq", i, { question: val })} />
                <div className="mt-3"><Area label="Risposta" value={f.answer} onChange={(val) => updItem("faq", i, { answer: val })} rows={3} /></div>
              </SectionCard>
            ))}
          </TabsContent>

          {/* SEO */}
          <TabsContent value="seo" className="space-y-6 pt-8">
            <Field label="Slug URL (/corsi/...)" value={c.slug} onChange={(v) => set("slug", v)} placeholder="generato dal titolo se vuoto" testid="f-slug" />
            <Field label="Meta title" value={c.meta_title} onChange={(v) => set("meta_title", v)} placeholder="Titolo per i motori di ricerca" testid="f-meta-title" />
            <Area label="Meta description" value={c.meta_description} onChange={(v) => set("meta_description", v)} placeholder="Descrizione per i motori di ricerca (max ~160 caratteri)" testid="f-meta-desc" />
          </TabsContent>
        </Tabs>
      </div>

      {/* Save bar */}
      <div className="fixed bottom-0 left-0 w-full bg-[#0B1220] z-30">
        <div className="px-6 md:px-10 max-w-[1100px] mx-auto py-4 flex items-center justify-between">
          <span className="text-sm text-[#F4F8FF]/60">{isNew ? "Nuovo corso" : "Modifica corso"}</span>
          <button
            data-testid="save-course-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#F4F8FF] text-[#0B1220] hover:bg-[#5B9DFF] transition-colors rounded-sm px-8 py-3 text-sm font-medium disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salva corso
          </button>
        </div>
      </div>
    </div>
  );
}
